/**
 * Auth module - Query functions (repositories)
 * Handles all database operations for profiles and auth
 */

import { supabase } from '@/integrations/supabase/client';
import { Profile, TradieProfile, UserRole } from '@/lib/types';
import { isUserRole } from '@/lib/roles';

/**
 * Fetch user profile by user ID
 */
export async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data as Profile | null;
}

/**
 * Fetch tradie profile by user ID
 */
export async function fetchTradieProfile(userId: string): Promise<TradieProfile | null> {
  const { data, error } = await supabase
    .from('tradie_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data as TradieProfile | null;
}

/**
 * Update user profile
 */
export async function updateProfile(userId: string, updates: Partial<Profile>): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as Profile;
}

/**
 * Update tradie profile
 */
export async function updateTradieProfile(
  userId: string,
  updates: Partial<TradieProfile>
): Promise<TradieProfile> {
  const { data, error } = await supabase
    .from('tradie_profiles')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as TradieProfile;
}

/**
 * Create tradie profile
 */
export async function createTradieProfile(
  userId: string,
  profile: Partial<TradieProfile>
): Promise<TradieProfile> {
  const { data, error } = await supabase
    .from('tradie_profiles')
    .insert({ user_id: userId, ...profile })
    .select()
    .single();

  if (error) throw error;
  return data as TradieProfile;
}

/**
 * Sign up user
 */
export async function signUp(
  email: string,
  password: string,
  fullName: string,
  role: UserRole
) {
  const redirectUrl = `${window.location.origin}/`;
  const canonicalRole: UserRole = isUserRole(role) ? role : 'property_manager';

  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: redirectUrl,
      data: {
        full_name: fullName,
        role: canonicalRole,
      },
    },
  });

  return { error };
}

/**
 * Sign in user
 */
export async function signIn(email: string, password: string) {
  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  return { error };
}

/**
 * Sign out user
 */
export async function signOut() {
  return supabase.auth.signOut();
}

/**
 * Get current session
 */
export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(callback: Parameters<typeof supabase.auth.onAuthStateChange>[0]) {
  return supabase.auth.onAuthStateChange(callback);
}
